import { useForm } from "react-hook-form";
import { zodResolver } from "@hookform/resolvers/zod";
import { insertHrSchema, type Hr } from "db/schema"; // Define the schema for the hr table
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import {
  Form,
  FormControl,
  FormField,
  FormItem,
  FormLabel,
  FormMessage,
} from "@/components/ui/form";
import { useToast } from "@/hooks/use-toast";
import { supabase } from "../../supabaseClient";

type HrFormProps = {
  onSuccess: (newHr: Hr) => void;
  initialData?: Hr | null;
};

export default function HrForm({
  onSuccess,
  initialData,
}: HrFormProps) {
  const { toast } = useToast();
  const form = useForm({
    resolver: zodResolver(insertHrSchema), // Use a schema for the HR table
    defaultValues: initialData || {
      name: "",
      phone: "",
      email: "",
    },
  });

  const onSubmit = async (values: any) => {
    try {
      const transformedValues = {
        ...values,
      };

      const tableName = "hr"; // Use the hr table in Supabase
      const { data, error } = initialData
        ? await supabase
            .from(tableName)
            .update(transformedValues)
            .eq("id", initialData.id)
            .select()
        : await supabase
            .from(tableName)
            .insert([transformedValues])
            .select();

      if (error) {
        console.error("Supabase error:", error);
        throw new Error(error.message || "Failed to save HR");
      }

      toast({
        title: "Success",
        description: initialData
          ? "HR updated successfully"
          : "HR created successfully",
      });

      if (data && data[0]) {
        onSuccess(data[0]);
      }
    } catch (error: any) {
      console.error("HR form error:", error);
      toast({
        variant: "destructive",
        title: "Error",
        description: error.message || "Failed to save HR",
      });
    }
  };

  return (
    <Form {...form}>
      <form onSubmit={form.handleSubmit(onSubmit)} className="space-y-6">
        <FormField
          control={form.control}
          name="name"
          render={({ field }) => (
            <FormItem>
              <FormLabel>Name</FormLabel>
              <FormControl>
                <Input {...field} id="name" />
              </FormControl>
              <FormMessage />
            </FormItem>
          )}
        />

<FormField
          control={form.control}
          name="phone"
          render={({ field }) => (
            <FormItem>
              <FormLabel>Phone</FormLabel>
              <FormControl>
                <Input
                  type="tel"
                  {...field}
                  pattern="[0-9]{10}"
                />
              </FormControl>
              <FormMessage />
            </FormItem>
          )}
        />

        <FormField
          control={form.control}
          name="email"
          render={({ field }) => (
            <FormItem>
              <FormLabel>Email</FormLabel>
              <FormControl>
                <Input type="email" {...field} id="email" />
              </FormControl>
              <FormMessage />
            </FormItem>
          )}
        />

        <Button type="submit" className="w-full">
          {initialData ? "Update HR" : "Create HR"}
        </Button>
      </form>
    </Form>
  );
}
